import { useQuery } from '@tanstack/react-query';
import { ChevronLeft, ChevronRight, Plus, Video } from 'lucide-react';
import { toast } from 'sonner';
import { useEffect, useMemo, useRef, useState } from 'react';
import { AppointmentDetailDialog, AppointmentDialog, techColor, useTechnicians, type AppointmentDefaults } from '@/components/appointments';
import { Tabs } from '@/components/tickets';
import { Button } from '@/components/ui/button';
import { PageHeader } from '@/components/ui/form-field';
import { Card, Switch } from '@/components/ui/primitives';
import { api } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import type { AppointmentItem } from '@/lib/types';
import { cn } from '@/lib/utils';

type View = 'day' | 'week' | 'month';
const VIEW_KEY = 'sp-calendar-view';
const TECH_KEY = 'sp-calendar-techs';
const HOUR_PX = 48;
const OUTLOOK_KEY = 'sp-calendar-outlook';

/** Termin im Kalender: aus dem Portal oder (nur lesend) aus Outlook. */
type CalItem = AppointmentItem & { outlook?: { allDay: boolean; showAs: string; location: string | null } };
interface OutlookResponse {
  connected: boolean;
  items: { id: string; technicianId: string; title: string; start: string; end: string; allDay: boolean; showAs: string; location: string | null }[];
  errors: { technicianId: string; message: string }[];
}
const SHOW_AS: Record<string, string> = { busy: 'Gebucht', tentative: 'Mit Vorbehalt', oof: 'Abwesend', workingElsewhere: 'Woanders tätig' };

const startOfDay = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate());
const addDays = (d: Date, n: number) => new Date(d.getFullYear(), d.getMonth(), d.getDate() + n);
const startOfWeek = (d: Date) => addDays(startOfDay(d), -((d.getDay() + 6) % 7));
const sameDay = (a: Date, b: Date) => a.toDateString() === b.toDateString();

const fmtDay = new Intl.DateTimeFormat('de-DE', { weekday: 'short', day: 'numeric', month: 'numeric' });
const fmtLong = new Intl.DateTimeFormat('de-DE', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
const fmtMonth = new Intl.DateTimeFormat('de-DE', { month: 'long', year: 'numeric' });
const fmtTime = new Intl.DateTimeFormat('de-DE', { hour: '2-digit', minute: '2-digit' });

function readStorage<T>(key: string, fallback: T): T {
  try {
    const v = localStorage.getItem(key);
    return v ? (JSON.parse(v) as T) : fallback;
  } catch {
    return fallback;
  }
}
function writeStorage(key: string, value: unknown) {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch {
    /* Speicher nicht verfügbar */
  }
}

function range(view: View, anchor: Date) {
  if (view === 'day') return { from: startOfDay(anchor), to: addDays(startOfDay(anchor), 1) };
  if (view === 'week') return { from: startOfWeek(anchor), to: addDays(startOfWeek(anchor), 7) };
  const first = new Date(anchor.getFullYear(), anchor.getMonth(), 1);
  const from = startOfWeek(first);
  return { from, to: addDays(from, 42) };
}

/** Verteilt sich überschneidende Termine eines Tages auf Spalten. */
function layoutDay(items: CalItem[], dayStart: Date) {
  const dayEnd = addDays(dayStart, 1);
  const sorted = items
    .filter((a) => new Date(a.startAt) < dayEnd && new Date(a.endAt) > dayStart)
    .sort((a, b) => a.startAt.localeCompare(b.startAt) || b.endAt.localeCompare(a.endAt));
  const lanes: number[] = [];
  const placed = sorted.map((a) => {
    const s = Math.max(new Date(a.startAt).getTime(), dayStart.getTime());
    const e = Math.min(new Date(a.endAt).getTime(), dayEnd.getTime());
    let lane = lanes.findIndex((end) => end <= s);
    if (lane < 0) lane = lanes.push(e) - 1;
    else lanes[lane] = e;
    return { a, s, e, lane };
  });
  return placed.map((p) => {
    const overlapping = placed.filter((o) => o.s < p.e && o.e > p.s);
    const lanesUsed = Math.max(...overlapping.map((o) => o.lane)) + 1;
    return { ...p, lanes: lanesUsed };
  });
}

function TimeGrid({
  days,
  items,
  colorOf,
  onOpen,
  onCreate,
}: {
  days: Date[];
  items: CalItem[];
  colorOf: (a: CalItem) => string;
  onOpen: (a: CalItem) => void;
  onCreate?: (start: Date) => void;
}) {
  const scroller = useRef<HTMLDivElement>(null);
  useEffect(() => {
    scroller.current?.scrollTo({ top: 7 * HOUR_PX });
  }, []);
  const now = new Date();

  return (
    <div className="flex min-h-0 flex-col">
      <div className="grid border-b" style={{ gridTemplateColumns: `3.5rem repeat(${days.length}, minmax(0, 1fr))` }}>
        <div />
        {days.map((d) => (
          <div key={d.toISOString()} className={cn('border-l px-2 py-2 text-center text-sm font-medium', sameDay(d, now) && 'text-primary')}>
            {days.length === 1 ? fmtLong.format(d) : fmtDay.format(d)}
            {items
              .filter((a) => a.outlook?.allDay && new Date(a.startAt) < addDays(d, 1) && new Date(a.endAt) > d)
              .map((a) => (
                <button key={a.id} type="button" onClick={() => onOpen(a)} className="mt-1 block w-full truncate rounded border border-dashed px-1 text-left text-xs font-normal text-muted-foreground hover:bg-muted">
                  {a.title}
                </button>
              ))}
          </div>
        ))}
      </div>
      <div ref={scroller} className="max-h-[70vh] overflow-y-auto">
        <div className="relative grid" style={{ gridTemplateColumns: `3.5rem repeat(${days.length}, minmax(0, 1fr))`, height: 24 * HOUR_PX }}>
          <div className="relative">
            {Array.from({ length: 24 }, (_, h) => (
              <span key={h} className="absolute right-2 -translate-y-1/2 text-xs text-muted-foreground" style={{ top: h * HOUR_PX }}>
                {h > 0 && `${String(h).padStart(2, '0')}:00`}
              </span>
            ))}
          </div>
          {days.map((d) => (
            <div key={d.toISOString()} className="relative border-l">
              {Array.from({ length: 24 }, (_, h) => (
                <button
                  key={h}
                  type="button"
                  disabled={!onCreate}
                  aria-label={onCreate ? `Termin am ${fmtDay.format(d)} um ${h} Uhr planen` : undefined}
                  className="absolute inset-x-0 border-t border-border/60 enabled:hover:bg-primary/5"
                  style={{ top: h * HOUR_PX, height: HOUR_PX }}
                  onClick={() => onCreate?.(new Date(d.getFullYear(), d.getMonth(), d.getDate(), h))}
                />
              ))}
              {sameDay(d, now) && (
                <div className="pointer-events-none absolute inset-x-0 z-20 border-t-2 border-destructive" style={{ top: ((now.getHours() * 60 + now.getMinutes()) / 60) * HOUR_PX }} />
              )}
              {layoutDay(items.filter((a) => !a.outlook?.allDay), d).map(({ a, s, e, lane, lanes }) => {
                const top = ((s - d.getTime()) / 3_600_000) * HOUR_PX;
                const height = Math.max(((e - s) / 3_600_000) * HOUR_PX, 20);
                const color = colorOf(a);
                return (
                  <button
                    key={a.id}
                    type="button"
                    onClick={() => onOpen(a)}
                    className={cn(
                      'absolute z-10 overflow-hidden rounded-md border-l-4 px-1.5 py-0.5 text-left text-xs transition-shadow hover:shadow-md',
                      a.outlook ? 'border border-dashed text-muted-foreground' : 'shadow-sm',
                    )}
                    title={a.outlook ? 'Outlook-Termin (nur lesend)' : undefined}
                    style={{
                      top,
                      height,
                      left: `calc(${(lane / lanes) * 100}% + 2px)`,
                      width: `calc(${100 / lanes}% - 4px)`,
                      borderLeftColor: color,
                      background: a.outlook ? `repeating-linear-gradient(135deg, var(--color-card), var(--color-card) 6px, color-mix(in oklab, ${color} 10%, var(--color-card)) 6px, color-mix(in oklab, ${color} 10%, var(--color-card)) 12px)` : `color-mix(in oklab, ${color} 14%, var(--color-card))`,
                    }}
                  >
                    <span className="block truncate font-medium">
                      {a.isOnlineMeeting && <Video className="mr-1 inline size-3" />}
                      {a.title}
                    </span>
                    <span className="block truncate text-muted-foreground">
                      {fmtTime.format(new Date(a.startAt))}–{fmtTime.format(new Date(a.endAt))} · {a.technicians.map((t) => t.name.split(' ')[0]).join(', ')}
                      {a.outlook && ' · Outlook'}
                    </span>
                  </button>
                );
              })}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function MonthGrid({
  anchor,
  from,
  items,
  colorOf,
  onOpen,
  onDay,
}: {
  anchor: Date;
  from: Date;
  items: CalItem[];
  colorOf: (a: CalItem) => string;
  onOpen: (a: CalItem) => void;
  onDay: (d: Date) => void;
}) {
  const days = Array.from({ length: 42 }, (_, i) => addDays(from, i));
  const now = new Date();
  return (
    <div>
      <div className="grid grid-cols-7 border-b text-center text-xs font-medium text-muted-foreground">
        {['Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa', 'So'].map((d) => (
          <div key={d} className="py-2">
            {d}
          </div>
        ))}
      </div>
      <div className="grid grid-cols-7">
        {days.map((d) => {
          const list = items.filter((a) => new Date(a.startAt) < addDays(d, 1) && new Date(a.endAt) > d);
          return (
            <div key={d.toISOString()} className={cn('min-h-24 border-b border-r p-1', d.getMonth() !== anchor.getMonth() && 'bg-muted/30 text-muted-foreground')}>
              <button
                type="button"
                onClick={() => onDay(d)}
                className={cn('mb-1 inline-flex size-6 items-center justify-center rounded-full text-xs hover:bg-muted', sameDay(d, now) && 'bg-primary text-primary-foreground hover:bg-primary')}
                aria-label={`${fmtLong.format(d)} anzeigen`}
              >
                {d.getDate()}
              </button>
              <div className="grid gap-0.5">
                {list.slice(0, 3).map((a) => (
                  <button key={a.id} type="button" onClick={() => onOpen(a)} className={cn('flex items-center gap-1 truncate rounded px-1 text-left text-xs hover:bg-muted', a.outlook && 'italic text-muted-foreground')}>
                    <span className="size-1.5 shrink-0 rounded-full" style={{ background: colorOf(a) }} />
                    <span className="truncate">
                      {a.outlook?.allDay ? '' : fmtTime.format(new Date(a.startAt))} {a.title}
                    </span>
                  </button>
                ))}
                {list.length > 3 && (
                  <button type="button" onClick={() => onDay(d)} className="px-1 text-left text-xs text-muted-foreground hover:underline">
                    +{list.length - 3} weitere
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export function CalendarPage() {
  const { can, me } = useAuth();
  const [view, setView] = useState<View>(() => readStorage<View>(VIEW_KEY, 'week'));
  const [anchor, setAnchor] = useState(() => new Date());
  const [selected, setSelected] = useState<string[] | null>(() => readStorage<string[] | null>(TECH_KEY, null));
  const [open, setOpen] = useState<AppointmentItem | null>(null);
  const [showOutlook, setShowOutlook] = useState(() => readStorage<boolean>(OUTLOOK_KEY, true));
  const [create, setCreate] = useState<AppointmentDefaults | null>(null);
  const { data: techs } = useTechnicians();
  const { from, to } = range(view, anchor);

  useEffect(() => writeStorage(VIEW_KEY, view), [view]);
  useEffect(() => writeStorage(TECH_KEY, selected), [selected]);
  useEffect(() => writeStorage(OUTLOOK_KEY, showOutlook), [showOutlook]);

  const { data, isFetching } = useQuery({
    queryKey: ['appointments', 'range', from.toISOString(), to.toISOString()],
    queryFn: () => api<{ items: AppointmentItem[] }>(`/appointments?from=${from.toISOString()}&to=${to.toISOString()}`),
  });

  const allTechs = techs?.items ?? [];
  const activeTechs = selected ?? allTechs.map((t) => t.id);
  const outlookIds = activeTechs.slice(0, 20).join(',');
  const { data: outlook } = useQuery({
    queryKey: ['appointments', 'outlook', from.toISOString(), to.toISOString(), outlookIds],
    queryFn: () => api<OutlookResponse>(`/appointments/outlook?from=${from.toISOString()}&to=${to.toISOString()}&technicianIds=${outlookIds}`),
    enabled: showOutlook && outlookIds.length > 0,
    staleTime: 60_000,
  });
  const items = useMemo<CalItem[]>(() => {
    const own: CalItem[] = (data?.items ?? []).filter((a) => selected === null || a.technicians.some((t) => selected.includes(t.id)));
    if (!showOutlook || !outlook?.connected) return own;
    const name = (id: string) => allTechs.find((t) => t.id === id)?.name ?? '';
    const ext: CalItem[] = outlook.items.map((o) => ({
      id: `outlook:${o.id}`,
      title: o.title,
      description: null,
      location: o.location,
      startAt: o.start,
      endAt: o.end,
      status: 'CONFIRMED',
      isOnlineMeeting: false,
      onlineMeetingUrl: null,
      technicians: [{ id: o.technicianId, name: name(o.technicianId) }],
      customerEmails: [],
      ticket: null,
      repair: null,
      customer: null,
      reminderMinutes: null,
      outlookSynced: true,
      syncError: null,
      createdAt: o.start,
      outlook: { allDay: o.allDay, showAs: o.showAs, location: o.location },
    }));
    return [...own, ...ext];
  }, [data, selected, showOutlook, outlook, allTechs]);
  const openItem = (a: CalItem) => {
    if (!a.outlook) return setOpen(a);
    const when = a.outlook.allDay ? 'ganztägig' : `${fmtTime.format(new Date(a.startAt))}–${fmtTime.format(new Date(a.endAt))}`;
    toast.info(`${a.title} (Outlook)`, { description: [`${a.technicians[0]?.name} · ${when}`, SHOW_AS[a.outlook.showAs], a.outlook.location].filter(Boolean).join(' · ') });
  };
  const colorOf = (a: CalItem) => techColor(a.technicians.find((t) => activeTechs.includes(t.id))?.id ?? a.technicians[0]?.id ?? '', allTechs);

  const step = (dir: 1 | -1) =>
    setAnchor((d) => (view === 'day' ? addDays(d, dir) : view === 'week' ? addDays(d, 7 * dir) : new Date(d.getFullYear(), d.getMonth() + dir, 1)));
  const title =
    view === 'day' ? fmtLong.format(anchor) : view === 'week' ? `${fmtDay.format(from)} – ${fmtDay.format(addDays(to, -1))}` : fmtMonth.format(anchor);
  const toggle = (id: string) => {
    const base = selected ?? allTechs.map((t) => t.id);
    const next = base.includes(id) ? base.filter((t) => t !== id) : [...base, id];
    setSelected(next.length === allTechs.length ? null : next);
  };
  const canCreate = can('calendar.manage');
  const createAt = (start: Date) =>
    setCreate({ start, technicianIds: selected && selected.length > 0 ? [selected[0]!] : me ? [me.user.id] : [] });

  return (
    <div>
      <PageHeader
        title="Kalender"
        description="Termine aller Mitarbeitenden; mit Microsoft 365 werden sie mit Outlook abgeglichen."
        actions={
          canCreate && (
            <Button onClick={() => createAt(new Date(anchor.getFullYear(), anchor.getMonth(), anchor.getDate(), Math.max(new Date().getHours() + 1, 8)))}>
              <Plus /> Termin
            </Button>
          )
        }
      />
      <div className="mb-4 flex flex-wrap items-center gap-2">
        <div className="flex items-center gap-1">
          <Button variant="outline" size="icon" onClick={() => step(-1)} aria-label="Zurück">
            <ChevronLeft />
          </Button>
          <Button variant="outline" onClick={() => setAnchor(new Date())}>
            Heute
          </Button>
          <Button variant="outline" size="icon" onClick={() => step(1)} aria-label="Weiter">
            <ChevronRight />
          </Button>
        </div>
        <h2 className="mr-auto text-lg font-semibold" aria-live="polite">
          {title}
          {isFetching && <span className="ml-2 text-xs font-normal text-muted-foreground">lädt…</span>}
        </h2>
        <Tabs
          value={view}
          onChange={setView}
          items={[
            { value: 'day', label: 'Tag' },
            { value: 'week', label: 'Woche' },
            { value: 'month', label: 'Monat' },
          ]}
        />
      </div>
      <div className="mb-3 flex flex-wrap items-center gap-3 text-sm">
        <label className="inline-flex items-center gap-2">
          <Switch checked={showOutlook} onCheckedChange={setShowOutlook} /> Outlook-Termine anzeigen
        </label>
        {showOutlook && outlook && !outlook.connected && <span className="text-xs text-muted-foreground">Microsoft 365 ist nicht verbunden (Administration → Microsoft 365).</span>}
        {showOutlook &&
          outlook?.errors.map((e) => (
            <span key={e.technicianId} className="text-xs text-destructive">
              Outlook-Kalender von {allTechs.find((t) => t.id === e.technicianId)?.name ?? 'Mitarbeiter'} nicht lesbar: {e.message}
            </span>
          ))}
      </div>
      {allTechs.length > 1 && (
        <div className="mb-4 flex flex-wrap gap-1.5" role="group" aria-label="Mitarbeitende filtern">
          {allTechs.map((t) => {
            const active = activeTechs.includes(t.id);
            return (
              <button
                key={t.id}
                type="button"
                aria-pressed={active}
                onClick={() => toggle(t.id)}
                className={cn('inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-sm transition-colors', active ? 'bg-card' : 'text-muted-foreground opacity-60 hover:opacity-100')}
              >
                <span className="size-2.5 rounded-full" style={{ background: techColor(t.id, allTechs) }} />
                {t.name}
              </button>
            );
          })}
          {selected !== null && (
            <Button variant="ghost" size="sm" onClick={() => setSelected(null)}>
              Alle anzeigen
            </Button>
          )}
        </div>
      )}
      <Card className="overflow-hidden">
        {view === 'month' ? (
          <MonthGrid
            anchor={anchor}
            from={from}
            items={items}
            colorOf={colorOf}
            onOpen={openItem}
            onDay={(d) => {
              setAnchor(d);
              setView('day');
            }}
          />
        ) : (
          <TimeGrid
            days={Array.from({ length: view === 'day' ? 1 : 7 }, (_, i) => addDays(from, i))}
            items={items}
            colorOf={colorOf}
            onOpen={openItem}
            onCreate={canCreate ? createAt : undefined}
          />
        )}
      </Card>
      <AppointmentDetailDialog appointment={open} onClose={() => setOpen(null)} />
      <AppointmentDialog open={create !== null} onClose={() => setCreate(null)} defaults={create ?? {}} />
    </div>
  );
}
